import { ApiRoutes, QueryError } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { Domain, SearchConsoleAddressChangingInfo, SiteSearchConsoleFormFields, SiteSearchConsoleQueryKeys } from '@/modules/Domains';
import { Site } from '@/modules/Sites';

export const useAddSearchConsoleAccountToSiteMutation = (siteId: string) =>
  buildMutation<void, QueryError, SiteSearchConsoleFormFields>(
    [SiteSearchConsoleQueryKeys.AddToSite, siteId],
    async (postData) => {
      await apiExecutor.post<void>(ApiRoutes.SiteDomainsSearchConsole.replace(':siteId', siteId), {
        ...postData
      });
    }
  );

export const useRemoveSearchConsoleAccountToSiteMutation = (siteId: string) =>
  buildMutation<void, QueryError, void>(
    [SiteSearchConsoleQueryKeys.UnsetFromSite, siteId],
    async () => {
      await apiExecutor.delete<void>(ApiRoutes.SiteDomainsSearchConsole.replace(':siteId', siteId));
    }
  );

export const useUpdateSearchConsoleAccountInSiteMutation = (siteId: string) =>
  buildMutation<void, QueryError, SiteSearchConsoleFormFields>(
    [SiteSearchConsoleQueryKeys.UpdateInSite, siteId],
    async (postData) => {
      await apiExecutor.patch<void>(ApiRoutes.SiteDomainsSearchConsole.replace(':siteId', siteId), {
        ...postData
      });
    }
  );

export const useVerifyGoogleDomainMutation = (siteId: string, domainId: string) =>
  buildMutation<Pick<Domain, 'isGoogleVerificated' | 'googleVerificationErrorMessage'>, QueryError, void>(
    [SiteSearchConsoleQueryKeys.VerifyGoogleDomain, siteId, domainId],
    async () => {
      const { data } = await apiExecutor.post(ApiRoutes.SiteDomainsSearchConsoleVerifyGoogle.replace(':siteId', siteId).replace(':domainId', domainId));
      return data;
    }
  );

export const useGenerateGoogleTokenDomainMutation = (siteId: string, domainId: string) =>
  buildMutation<Pick<Domain, 'googleVerificationToken'>, QueryError, void>(
    [SiteSearchConsoleQueryKeys.VerifyGoogleDomain, siteId, domainId],
    async () => {
      const { data } = await apiExecutor.post(ApiRoutes.SiteDomainsSearchConsoleGenerateToken.replace(':siteId', siteId).replace(':domainId', domainId));
      return data;
    }
  );

export const useAddInSearchConsoleDomainMutation = (siteId: string, domainId: string) =>
  buildMutation<Pick<Domain, 'isIncludedInSearchConsole'>, QueryError, void>(
    [SiteSearchConsoleQueryKeys.VerifyGoogleDomain, siteId, domainId],
    async () => {
      const { data } = await apiExecutor.post(ApiRoutes.SiteDomainsSearchConsoleAddDomain.replace(':siteId', siteId).replace(':domainId', domainId));
      return data;
    }
  );

export const useGetSitesSearchConsoleInfoQuery = (siteId: string) =>
  buildQuery<{
    searchConsoleAddressChanging: Site['searchConsoleAddressChanging'];
    isAutoRedirecting: Site['isAutoRegluingsEnabled'];
    haveSearchConsoleAccount: boolean;
  }, QueryError>([SiteSearchConsoleQueryKeys.GetInfo, siteId], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.SiteDomainsSearchConsole.replace(':siteId', siteId));
    return data;
  });

export const useUpdateRedirectingAutoProcessingSettingMutation = (siteId: string) =>
  buildMutation<void, QueryError, { isAutoRedirecting: boolean }>(
    [SiteSearchConsoleQueryKeys.UpdateRedirectingAutoProcessingSetting, siteId],
    async ({ isAutoRedirecting }) => {
      await apiExecutor.patch(ApiRoutes.SiteDomainsSearchConsoleRedirectingAutoProcessing.replace(':siteId', siteId), { isAutoRedirecting });
    }
  );

export const useAddRedirectingTaskMutation = (siteId: string) =>
  buildMutation<SearchConsoleAddressChangingInfo, QueryError, void>(
    [SiteSearchConsoleQueryKeys.AddRedirectingTask, siteId],
    async () => {
      const { data } = await apiExecutor.post(ApiRoutes.SiteDomainsSearchConsoleAddRedirectingTask.replace(':siteId', siteId));
      return data;
    }
  );

